// HAP-4635: keep canonical / description / Open Graph / Twitter tags in step with client-side
// navigation. The first paint gets these server-side from public/index.js (which is what bots
// read); this only updates them as the SPA moves between routes, so a pushState trip through the
// site never leaves the home description or a stale canonical behind.
//
// The section copy below MUST match SECTION_META in public/index.js.

const ORIGIN = 'https://churchandfamilylife.com'
const SITE = 'Church & Family Life'
const DEFAULT_IMAGE = `${ORIGIN}/images/og-default-cfl.jpg`

const SECTIONS = {
	'/': {
		title: SITE,
		description: 'Church & Family Life proclaims the sufficiency of Scripture for church and family life — resources, events, podcasts, and a network of faithful churches.',
	},
	'/store': {
		title: `${SITE} | Store`,
		description: 'Books and booklets from Church & Family Life on family worship, biblical manhood and womanhood, marriage, and the family-integrated church.',
	},
	'/events': {
		title: `${SITE} | Events`,
		description: 'Upcoming Church & Family Life conferences, retreats, and gatherings for fathers, mothers, families, and church leaders across the country.',
	},
	'/resources': {
		title: `${SITE} | Resources`,
		description: 'Free sermons, articles, and videos from Church & Family Life on family worship, marriage, parenting, and the sufficiency of Scripture.',
	},
	'/podcasts': {
		title: `${SITE} | Podcasts`,
		description: 'Church & Family Life podcasts on family worship, parenting, and church life — practical biblical teaching for fathers, mothers, and pastors.',
	},
	'/donate': {
		title: `${SITE} | Donate`,
		description: 'Support Church & Family Life. Your gift helps proclaim the sufficiency of Scripture and strengthen churches and families for the next generation.',
	},
	'/mission': {
		title: `Build What Lasts — 2026 Ministry Report | ${SITE}`,
		description: 'Church & Family Life 2026 Ministry Report. Proclaiming quiet faithfulness through the sufficiency of Scripture. Help us build what lasts in 2027.',
		image: `${ORIGIN}/images/mission/hero-worship.jpg`,
	},
}

function upsert(selector, create, attrs) {
	let el = document.head.querySelector(selector)
	if (!el) {
		el = document.createElement(create)
		document.head.appendChild(el)
	}
	for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
}

function setMeta(key, content, attr = 'property') {
	if (!content) return
	upsert(`meta[${attr}="${key}"]`, 'meta', { [attr]: key, content })
}

function normalizedPath(path) {
	const p = (path || '/').split('?')[0].split('#')[0].replace(/\/+$/, '')
	return p || '/'
}

export function applyRouteMeta(to, { firstLoad = false } = {}) {
	const path = normalizedPath(to.path)
	const section = SECTIONS[path]

	// Detail pages are rendered with entry-specific tags by the server on first load; don't
	// overwrite those with generic copy before the page has even mounted.
	if (firstLoad && !section) return

	const url = ORIGIN + (path === '/' ? '/' : path)
	upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: url })
	setMeta('og:url', url)

	if (section) {
		setMeta('description', section.description, 'name')
		setMeta('og:description', section.description)
		setMeta('twitter:description', section.description, 'name')
		setMeta('og:title', section.title)
		setMeta('twitter:title', section.title, 'name')
		setMeta('og:type', 'website')
		setMeta('og:image', section.image || DEFAULT_IMAGE)
		setMeta('twitter:image', section.image || DEFAULT_IMAGE, 'name')
	} else {
		// Detail page reached by client-side navigation: the component sets document.title once
		// its entry loads, so mirror that into og/twitter titles shortly after.
		setTimeout(() => {
			setMeta('og:title', document.title)
			setMeta('twitter:title', document.title, 'name')
		}, 1500)
	}
	setMeta('twitter:card', 'summary_large_image', 'name')
}
